import { ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { BaseQuestionComponent } from '../../question.component';
import { ReactiveFormsModule, UntypedFormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { QuestionBase } from '@jump-tech-frontend/domain';
import { I18nKeys } from '@jump-tech-frontend/app-config';
import { ImageAssetFactory } from '../../../domain/card';
import { ImageAsset, ImageAssetType } from '../../../domain/types';
import {
  BoundingBoxOverlayComponent,
  BoundingBoxOverlayPresenter,
  HttpImageComponent,
  ImageClassificationService
} from '@jump-tech-frontend/image-analysis';
import { ImageAssetComponent } from '../../image-asset.component';

import { NgxSpinnerModule } from 'ngx-spinner';
import { QuestionHintComponent, QuestionLabelContentComponent } from '@jump-tech-frontend/question-components';
import { MultiImageAiValidationUploadQuestionInputComponent } from './multi-image-ai-validation-upload-question-input.component';
import { ClassificationResult, classificationTypeOf, ImageWithMetadata } from '@jump-tech-frontend/domain';
import { firstValueFrom } from 'rxjs';
import { ValidationResultsComponent } from './validation-results/validation-results.component';
import { ValidationResultsPresenter } from './validation-results/validation-results.presenter';

@Component({
  selector: 'crds-question-multi-image-ai-validation-upload',
  imports: [
    ReactiveFormsModule,
    HttpImageComponent,
    ImageAssetComponent,
    MultiImageAiValidationUploadQuestionInputComponent,
    NgxSpinnerModule,
    QuestionHintComponent,
    QuestionLabelContentComponent,
    ValidationResultsComponent,
    BoundingBoxOverlayComponent
  ],
  providers: [ValidationResultsPresenter, BoundingBoxOverlayPresenter],
  templateUrl: './multi-image-ai-validation-upload-question.component.html',
  styleUrls: ['./multi-image-ai-validation-upload-question.component.scss']
})
export class MultiImageAiValidationUploadQuestionComponent extends BaseQuestionComponent implements OnChanges {
  private readonly imageAssetFactory = inject(ImageAssetFactory);
  private readonly imageClassificationService = inject(ImageClassificationService);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() override form: UntypedFormGroup;
  @Input() override question: QuestionBase<unknown>;
  @Input() i18ns: I18nKeys;
  @Input() deviceContext?: 'mobile' | 'desktop' = 'desktop';
  @Input() projectId?: string;

  images: ImageAsset[];
  uploadedImages: string[] = [];
  uploadedImagesMetaData: Map<number, ClassificationResult> = new Map();
  imageAssetType = ImageAssetType;
  clearInputValue = false;
  showBoundingBoxData = false;
  validationInProgressMap: Map<number, boolean> = new Map();
  showBoundingBoxMap: Map<number, boolean> = new Map();
  private readonly storedImagesBySource: Map<string, ImageWithMetadata> = new Map();
  private readonly analysisIdsBySource: Map<string, string> = new Map();
  private classificationQueue: Promise<unknown> = Promise.resolve();
  private readonly analysisInProgressValidator: ValidatorFn = (): ValidationErrors | null =>
    this.isAnyAnalysisInProgress() ? { analysisInProgress: true } : null;
  readonly imageSkeletonHeight = 450;

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.question || !Object.prototype.hasOwnProperty.call(changes, 'question')) {
      return;
    }
    if (this.value && !(this.value instanceof Array)) {
      setTimeout(() => {
        this.form?.get(this.question.key)?.patchValue([this.value]);
      });
    }
    this.images = this.imageAssetFactory.create(this.question.image, this.form);
    this.setUploadedImages();
    this.blockSubmitWhileAnalysing();
  }

  private blockSubmitWhileAnalysing(): void {
    const control = this.form?.get(this.question.key);
    if (control && !control.hasValidator(this.analysisInProgressValidator)) {
      control.addValidators(this.analysisInProgressValidator);
      control.updateValueAndValidity();
    }
  }

  private isAnyAnalysisInProgress(): boolean {
    return [...this.validationInProgressMap.values()].some(Boolean);
  }

  // TODO: parent-level question components in this folder are largely untested
  // (only a handful have specs). Worth adding coverage for the show/showIf
  // branches across the image question family — JMP-6503 was a regression that
  // a small spec on this getter would have caught.
  get show() {
    const showIfQuestionPopulated = this.question.showIf ? this.showIf : true;
    return (
      (this.question.showIfPopulated !== true || this.value) && showIfQuestionPopulated && this.question.show !== false
    );
  }

  async onImageUploaded($event: string) {
    if (!this.question.sort) {
      this.question.sort = 'desc';
    }
    const lastUploadedImage = $event;
    if (lastUploadedImage) {
      if (this.question.sort && this.question.sort === 'desc') {
        this.shiftProgressMapIndicesUp();
        this.shiftMapIndicesUp();
        this.shiftBoundingBoxMapIndicesUp();
        this.uploadedImages.unshift(lastUploadedImage);
        this.validationInProgressMap.set(0, true);
        this.form?.get(this.question.key)?.patchValue(this.uploadedImages);
        this.cdr.detectChanges();
      } else {
        this.uploadedImages.push(lastUploadedImage);
        this.validationInProgressMap.set(this.uploadedImages.length - 1, true);
        this.form?.get(this.question.key)?.patchValue(this.uploadedImages);
        this.cdr.detectChanges();
      }

      const result = await this.classifyInTurn(lastUploadedImage);
      this.showBoundingBoxData = true;

      if (result.analysisId) {
        this.analysisIdsBySource.set(lastUploadedImage, result.analysisId);
      }

      const index = this.uploadedImages.indexOf(lastUploadedImage);
      if (index === -1) {
        return;
      }

      this.validationInProgressMap.set(index, false);
      this.uploadedImagesMetaData.set(index, result);
      this.showBoundingBoxMap.set(index, true);

      this.patchFormWithWrappedImages();
    }
  }

  private classifyInTurn(imageBinary: string): Promise<ClassificationResult> {
    const classification = this.classificationQueue.then(() => this.classifyImage(imageBinary));
    this.classificationQueue = classification.catch(() => undefined);
    return classification;
  }

  private async classifyImage(imageBinary: string): Promise<ClassificationResult> {
    try {
      return await firstValueFrom(
        this.imageClassificationService.classifyImage({
          base64Image: imageBinary,
          deviceContext: this.deviceContext,
          classificationType: classificationTypeOf(this.question.imageClassification),
          projectId: this.projectId
        })
      );
    } catch (error: unknown) {
      // Handle API errors (500, network errors, etc.) and return error result
      return {
        status: 'error',
        success: false,
        equipmentType: '',
        confidence: 0,
        fuseCutoutConfidence: 0,
        consumerUnitConfidence: 0,
        isolatorSwitchConfidence: 0,
        confidenceThresholdMet: false,
        qualityScore: 0,
        qualityAcceptable: false,
        qualityIssues: [],
        warnings: [],
        error:
          errorMessageOf(error) ||
          this.i18ns?.aiValidation?.failedToValidateImage ||
          'Failed to validate image. Please try again.'
      };
    }
  }

  get hasUploadedImages() {
    return this.uploadedImages.length > 0;
  }

  /**
   * Get classification result for a given image index
   * @param index - The index of the image in the uploadedImages array
   * @returns The classification result or undefined if not found
   */
  getClassificationResult(index: number): ClassificationResult | undefined {
    return this.uploadedImagesMetaData.get(index);
  }

  /**
   * Check if validation is in progress for a specific image
   * @param index - The index of the image in the uploadedImages array
   * @returns True if validation is in progress for this image
   */
  isValidationInProgress(index: number): boolean {
    return this.validationInProgressMap.get(index) === true;
  }

  /**
   * Toggle bounding box visibility for a specific image
   * @param index - The index of the image in the uploadedImages array
   */
  toggleBoundingBox(index: number): void {
    const currentState = this.showBoundingBoxMap.get(index) ?? true;
    this.showBoundingBoxMap.set(index, !currentState);
  }

  /**
   * Check if bounding boxes are visible for a specific image
   * @param index - The index of the image in the uploadedImages array
   * @returns True if bounding boxes should be shown for this image
   */
  isBoundingBoxVisible(index: number): boolean {
    return this.showBoundingBoxMap.get(index) ?? true; // Default to visible
  }

  /**
   * Get formatted equipment type label from question config
   * Converts snake_case to Title Case for display (e.g., "fuse_cutout" → "Fuse Cutout")
   * Uses translation when available for specific equipment types
   * @returns Formatted equipment type label or "Detection" as fallback
   */
  getEquipmentTypeLabel(): string {
    if (!this.question?.imageClassification) {
      return this.i18ns?.aiValidation?.detection || 'Detection';
    }

    const aiI18n = this.i18ns?.aiValidation;

    // Check for known equipment type translations
    switch (this.question.imageClassification) {
      case 'fuse-cutout':
        return aiI18n?.fuseCutout || 'Fuse Cutout';
      case 'consumer-unit':
        return aiI18n?.consumerUnit || 'Consumer Unit';
      case 'isolator-switch':
        return aiI18n?.isolatorSwitch || 'Isolator Switch';
      default:
        // Convert snake_case to Title Case for unknown types
        return this.question.imageClassification
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
    }
  }

  /**
   * Get translated "Remove" button text
   * @returns Translated remove text or fallback
   */
  getRemoveLabel(): string {
    return this.i18ns?.aiValidation?.remove || 'Remove';
  }

  /**
   * Shift all Map indices up by 1 (when inserting at front with unshift)
   * Moves entries from high to low to avoid overwriting
   */
  private shiftMapIndicesUp(): void {
    const entries = Array.from(this.uploadedImagesMetaData.entries()).sort((a, b) => b[0] - a[0]);
    this.uploadedImagesMetaData.clear();
    entries.forEach(([index, result]) => {
      this.uploadedImagesMetaData.set(index + 1, result);
    });
  }

  /**
   * Shift all progress Map indices up by 1 (when inserting at front with unshift)
   * Moves entries from high to low to avoid overwriting
   */
  private shiftProgressMapIndicesUp(): void {
    const entries = Array.from(this.validationInProgressMap.entries());
    this.validationInProgressMap.clear();
    entries.forEach(([index, inProgress]) => {
      this.validationInProgressMap.set(index + 1, inProgress);
    });
  }

  /**
   * Shift all bounding box visibility Map indices up by 1 (when inserting at front with unshift)
   * Moves entries from high to low to avoid overwriting
   */
  private shiftBoundingBoxMapIndicesUp(): void {
    const entries = Array.from(this.showBoundingBoxMap.entries()).sort((a, b) => b[0] - a[0]);
    this.showBoundingBoxMap.clear();
    entries.forEach(([index, isVisible]) => {
      this.showBoundingBoxMap.set(index + 1, isVisible);
    });
  }

  /**
   * Shift all Map indices down by 1 starting from a given index (when deleting an item)
   * @param fromIndex - Start shifting from this index
   */
  private shiftMapIndicesDown(fromIndex: number): void {
    const maxIndex = Math.max(...Array.from(this.uploadedImagesMetaData.keys()));
    for (let i = fromIndex; i <= maxIndex; i++) {
      const nextResult = this.uploadedImagesMetaData.get(i + 1);
      if (nextResult) {
        this.uploadedImagesMetaData.set(i, nextResult);
      } else {
        this.uploadedImagesMetaData.delete(i);
      }
    }
  }

  /**
   * Shift all progress Map indices down by 1 starting from a given index (when deleting an item)
   * @param fromIndex - Start shifting from this index
   */
  private shiftProgressMapIndicesDown(fromIndex: number): void {
    const maxIndex = Math.max(...Array.from(this.validationInProgressMap.keys()));
    for (let i = fromIndex; i <= maxIndex; i++) {
      const nextInProgress = this.validationInProgressMap.get(i + 1);
      if (nextInProgress !== undefined) {
        this.validationInProgressMap.set(i, nextInProgress);
      } else {
        this.validationInProgressMap.delete(i);
      }
    }
  }

  /**
   * Shift all bounding box visibility Map indices down by 1 starting from a given index (when deleting an item)
   * @param fromIndex - Start shifting from this index
   */
  private shiftBoundingBoxMapIndicesDown(fromIndex: number): void {
    const maxIndex = Math.max(...Array.from(this.showBoundingBoxMap.keys()));
    for (let i = fromIndex; i <= maxIndex; i++) {
      const nextIsVisible = this.showBoundingBoxMap.get(i + 1);
      if (nextIsVisible !== undefined) {
        this.showBoundingBoxMap.set(i, nextIsVisible);
      } else {
        this.showBoundingBoxMap.delete(i);
      }
    }
  }

  /**
   * Wrap uploaded images with their analysis id and patch form
   * Creates ImageWithMetadata objects for images that have classification results
   */
  private patchFormWithWrappedImages(): void {
    const wrappedImages = this.uploadedImages.map((img, idx) =>
      this.toFormValue(img, this.uploadedImagesMetaData.get(idx))
    );
    this.form?.get(this.question.key)?.patchValue(wrappedImages);
  }

  private toFormValue(img: string, metadata: ClassificationResult | undefined): ImageWithMetadata | string {
    const analysisId = metadata?.analysisId ?? this.analysisIdsBySource.get(img);

    if (metadata && !isUnanalysed(metadata, analysisId)) {
      return { imageData: img, analysisId };
    }

    const stored = this.storedImagesBySource.get(img);
    if (stored) {
      return stored;
    }

    // Return plain string for images without metadata (backward compatibility)
    return img;
  }

  /**
   * Get image source for display from form value
   * Extracts imageData from wrapped objects or returns string directly
   * @param index - Index of the image in the form array
   * @returns Image source string for display
   */
  getImageSource(index: number): string {
    const formValue = this.form?.get(this.question.key)?.value;

    if (!formValue || !formValue[index]) {
      return '';
    }

    const value = formValue[index];

    // Handle both wrapped objects and plain strings for backward compatibility
    if (typeof value === 'object') {
      // Object - extract imageData if it exists and is a string
      if (value.imageData && typeof value.imageData === 'string') {
        return value.imageData;
      }
      // Object but no valid imageData - return empty string to prevent [object Object]
      return '';
    }

    // Plain string - return as is
    if (typeof value === 'string') {
      return value;
    }

    // Unexpected type
    return '';
  }

  handleMetadataLoaded(data: ClassificationResult | null | undefined, index: number): void {
    if (data) {
      // Store metadata for this image
      this.uploadedImagesMetaData.set(index, data);

      // Mark validation as complete (no longer in progress)
      this.validationInProgressMap.set(index, false);

      // Show bounding boxes by default when metadata is loaded
      this.showBoundingBoxMap.set(index, true);

      // Trigger change detection so child components (validation-results, bounding-box-overlay) update
      this.cdr.detectChanges();
    } else {
      console.log('No metadata found for image', index);
    }
  }

  clearImage(index: number): void {
    this.uploadedImagesMetaData.delete(index);
    this.shiftMapIndicesDown(index);

    this.validationInProgressMap.delete(index);
    this.shiftProgressMapIndicesDown(index);

    this.showBoundingBoxMap.delete(index);
    this.shiftBoundingBoxMapIndicesDown(index);

    this.uploadedImages.splice(index, 1);

    this.patchFormWithWrappedImages();
    this.cleanInputValue();
  }

  cleanInputValue(): void {
    this.clearInputValue = true;
    setTimeout(() => (this.clearInputValue = false));
  }

  setUploadedImages(): void {
    const values =
      this.form.get(this.question.key) && this.form?.get(this.question.key)?.value
        ? this.form?.get(this.question.key)?.value
        : [];

    // Extract plain image strings from potentially wrapped objects
    this.uploadedImages = values.map((value: ImageWithMetadata | string) => {
      if (value && typeof value === 'object' && 'imageData' in value) {
        this.rememberStoredImage(value);
        return value.imageData;
      }
      return value;
    });
  }

  private rememberStoredImage(value: ImageWithMetadata): void {
    if (typeof value.imageData !== 'string') {
      return;
    }

    this.storedImagesBySource.set(value.imageData, value);
    if (value.analysisId) {
      this.analysisIdsBySource.set(value.imageData, value.analysisId);
    }
  }
}

function isUnanalysed(metadata: ClassificationResult, analysisId: string | undefined): boolean {
  return metadata.status === 'error' && analysisId === undefined;
}

function messageOf(value: unknown): string | undefined {
  if (typeof value !== 'object' || value === null || !('message' in value)) {
    return undefined;
  }
  return typeof value.message === 'string' ? value.message : undefined;
}

function errorMessageOf(error: unknown): string | undefined {
  const httpBody = typeof error === 'object' && error !== null && 'error' in error ? error.error : undefined;
  return messageOf(httpBody) || messageOf(error);
}
